import { useRef, useEffect, useState, useMemo } from "react";
import { Card, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
// import { getCitiesByStateFromDashboard } from '../../utils/locationData';
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../utils/seoConfig";
import "./Investment.scss";
import {
  getAllCities,
  getAllStates,
  getRoles,
} from "../../redux/slices/authSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getinvestmentReports,
  getinvestmentReportsInputs,
} from "../../redux/slices/investmentSlice";
// import { useCallback } from "react";
import debounce from "lodash.debounce";
import { allowedRolesForFree } from "../../constants";
import { useForm, Controller } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  activityEnd,
  activityStart,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";

const investmentFieldSchema = Yup.object().shape({
  salary: Yup.number()
    .typeError("Salary must be a number")
    .positive("Salary must be positive")
    .required("Salary is required"),

  contribution_rate: Yup.number()
    .typeError("Contribution rate must be a number")
    .min(1, "Contribution rate should be between 1 and 100")
    .max(100, "Contribution rate should be between 1 and 100")
    .required("Contribution rate is required"),

  current_age: Yup.number()
    .typeError("Current age must be a number")
    .min(20, "Age must be more than 20 years old")
    .required("Current age is required"),

  retirement_age: Yup.number()
    .typeError("Retirement age must be a number")
    .max(65, '"Retirement age" must be less than or equal to 65')
    .when("current_age", (current_age, schema) => {
      if (current_age) {
        return schema
          .moreThan(
            current_age,
            "Retirement Age should be more than Current Age",
          )
          .min(40, '"Retirement age" must be greater than or equal to 40');
      }
      return schema.min(
        40,
        '"Retirement age" must be greater than or equal to 40',
      );
    })
    .required("Retirement age is required"),

  rate_of_return: Yup.number()
    .typeError("Rate of return must be a number")
    .min(5, "Return rate must be between 5 and 15")
    .max(15, "Return rate must be between 5 and 15")
    .required("Rate of return is required"),
});

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
);

const Investment = ({ selectedCities }) => {
  const {
    control, // Used to connect form inputs
    watch, // Watch form values in real-time
    setValue, // Programmatically set form values
    formState: { errors }, // Contains validation errors for each field
    trigger, // Manually trigger validation
  } = useForm({
    resolver: yupResolver(investmentFieldSchema), // Connect Yup schema
    mode: "onChange", // Validate on every change for real-time feedback
    defaultValues: {
      salary: 50000,
      contribution_rate: 10,
      current_age: 20,
      retirement_age: 45,
      rate_of_return: 10,
    },
  });

  const formData = watch();

  // Dummy chart data
  const chartRef = useRef(null);

  // const {
  //   formState: { errors },
  // } = useForm({
  //   mode: 'all', // Validate on change for real-time feedback
  //   defaultValues: {},
  // });

  const toggleDataset = () => {
    console.log("Toggling dataset visibility");

    const chart = chartRef.current; // chart is already the Chart.js instance
    if (!chart) {
      console.log("No chart instance found");
      return;
    }

    const meta = chart.getDatasetMeta(0);
    meta.hidden = meta.hidden === null ? !chart.isDatasetVisible(0) : null;
    chart.update();
    // console.log("Toggled dataset visibility", chart);
  };

  const store = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [localCities, setLocalCities] = useState({
    current: { state: null, city: null },
    destination: { state: null, city: null },
  });

  const [activeIndex, setActiveIndex] = useState();
  const [graphData, setGraphData] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedState, setSelectedState] = useState();
  const [selectedRole, setSelectedRole] = useState();
  const [selectedCity, setSelectedCity] = useState();

  const handleRoleChange = (selectedOption) => {
    setSelectedRole(selectedOption?.value || null);
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption?.code || null);
  };

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption?.value || null);
  };

  useEffect(() => {
    setLocalCities(
      selectedCities || {
        current: { state: null, city: null },
        destination: { state: null, city: null },
      },
    );
  }, [selectedCities]);

  const debouncedDispatch = useMemo(
    () =>
      debounce((inputData) => {
        dispatch(getinvestmentReports(inputData));
      }, 500),
    [dispatch],
  );

  const investmentDataStore = useSelector((state) => state.investment);

  useEffect(() => {
    if (selectedRole && selectedState && selectedCity) {
      const payload = {
        role: selectedRole,
        state: selectedState,
        city: selectedCity,
      };
      dispatch(getinvestmentReportsInputs(payload));
    }
  }, [selectedRole, selectedState, selectedCity, dispatch]);

  const inputsData = investmentDataStore.investmentReportInputs;

  useEffect(() => {
    const data = inputsData?.data;
    if (data && Object.keys(data).length > 0) {
      setValue("salary", data.salary);
      setValue("contribution_rate", data.contribution_rate);
      setValue("current_age", data.start_age);
      setValue("retirement_age", data.end_age);
      setValue("rate_of_return", data.rate_of_return);
    }
  }, [inputsData, setValue]);

  useEffect(() => {
    const validateAndDispatch = async () => {
      const isValid = await trigger();
      if (isValid) {
        const payload = {
          salary: Number(formData.salary),
          contribution_rate: Number(formData.contribution_rate),
          start_age: Number(formData.current_age),
          end_age: Number(formData.retirement_age),
          interest_rate: Number(formData.rate_of_return),
        };
        debouncedDispatch(payload);
      }
    };
    validateAndDispatch();

    return () => {
      debouncedDispatch.cancel();
    };
  }, [
    formData.salary,
    formData.contribution_rate,
    formData.current_age,
    formData.retirement_age,
    formData.rate_of_return,
    debouncedDispatch,
    trigger,
  ]);

  const investmentData = investmentDataStore.investmentReport.data;
  // console.log(investmentData);

  useEffect(() => {
    setGraphData(
      (investmentData || []).map((item, index) => ({
        value: Number(item?.savings ?? 0),
        label: String(item?.age ?? ""),
        index,
      })),
    );
  }, [investmentData]);

  // Track Activity
  usePageTracking("INVESTMENT");
  useEffect(() => {
    const payload = {
      page_name: "INVESTMENT",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "INVESTMENT",
        }),
      );
    };
  }, [dispatch]);

  

  const chartData = {
    labels: graphData.map((item, index) => {
      return item.label;
    }),
    datasets: [
      {
        label: "Project Balance",
        data: graphData?.map((item, index) => {
          // console.log(item, "Item Of Chart")
          return item?.value;
        }),
        borderColor: "#0195D3",
        backgroundColor: "#0195D3",
        tension: 0.4,
        pointRadius: 6,
        pointBackgroundColor: "#fff",
        pointBorderColor: "#0195D3",
        pointBorderWidth: 1,
        borderWidth: 2,
      },
    ],
  };

  useEffect(() => {
    dispatch(getAllStates());
    // dispatch(getAllCities());
    dispatch(getRoles());
    // dispatch(getinvestmentReports(data1))
  }, [dispatch]);

  useEffect(() => {
    if (selectedState) {
      dispatch(getAllCities({ search_state: selectedState }));
    }
  }, [dispatch, selectedState]);

  // Create flat cities array for compatibility
  // const allCities = useMemo(() => {
  //   const cities = [];
  //   Object.keys(citiesByState).forEach(state => {
  //     citiesByState[state].forEach(city => {
  //       cities.push({ ...city, state });
  //     });
  //   });
  //   return cities;
  // }, [citiesByState]);

  useEffect(() => {
    if (localCities?.destination?.state?.code) {
      dispatch(
        getAllCities({
          search_state: localCities.destination.state.code,
        }),
      );
    }
  }, [dispatch, localCities?.destination?.state?.code]);

  useEffect(() => {
    if (store?.stateItems?.states?.length) {
      const options = store.stateItems.states.map((state) => ({
        value: state.id,
        label: state.state_full_name || state.state,
        code: state.state,
      }));
      setStateOptions(options);
    }

    if (store?.cityItems?.cities?.length) {
      const options = store.cityItems.cities.map((cityObj) => ({
        value: cityObj.city,
        label: cityObj.city,
      }));
      setCityOptions(options);
    }
  }, [store?.stateItems?.states, store?.cityItems?.cities]);

  const inputFields = [
    { label: "Current salary", key: "salary", placeholder: "salary" },
    {
      label: "Contribution rate (%)",
      key: "contribution_rate",
      placeholder: "rate",
    },
    { label: "Current age", key: "current_age", placeholder: "age" },
    { label: "Retirement age", key: "retirement_age", placeholder: "age" },
    { label: "Rate of return", key: "rate_of_return", placeholder: "rate" },
  ];

  const formatNumber = (number) => {
    const parsedNumber = parseInt(number);
    if (isNaN(parsedNumber)) return "$0";

    const formattedNumber = new Intl.NumberFormat().format(
      Math.abs(parsedNumber),
    );

    return parsedNumber < 0 ? `-$${formattedNumber}` : `$${formattedNumber}`;
  };

  return (
    <div className="investment-content">
      <NoIndexSEO title={seoConfig.invest.title} />
      <div className="app-page-title">
        <div className="page-title-heading">
          <h3 className="page-title">Investment</h3>
        </div>
      </div>
      <div className="investment-card-container">
        <Card>
          {/* First row - Selects */}
          <Row>
            <Col md={4} sm={12} className="invest-select-box">
              <Form.Group className="form-group">
                <Form.Label className="col-label">Nursing Role</Form.Label>
                <Select
                  placeholder="Select Role"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={[
                    { value: "", label: "Select Role" },
                    ...(store?.roleItems?.describe_roles?.map((role) => {
                      const currentPlan = store?.user?.plan?.name;
                      const isAllowed =
                        currentPlan !== "Free" ||
                        allowedRolesForFree.includes(role.name) ||
                        role.name === "Others";
                      return {
                        value: role.id,
                        label: role.name,
                        isDisabled: !isAllowed,
                      };
                    }) || []),
                  ]}
                  onChange={(selectedOption) => {
                    const currentPlan = store?.user?.plan?.name;
                    const selectedRole = store?.roleItems?.describe_roles?.find(
                      (r) => r.id === selectedOption?.value,
                    );
                    if (
                      currentPlan === "Free" &&
                      selectedRole &&
                      !allowedRolesForFree.includes(selectedRole.name) &&
                      selectedRole.name !== "Others"
                    ) {
                      return;
                    }
                    handleRoleChange(selectedOption);
                  }}
                  isOptionDisabled={(option) => option.isDisabled}
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "#fff",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                      color: state.isDisabled ? "#999" : "#091F28",
                      cursor: state.isDisabled ? "not-allowed" : "pointer",
                      opacity: state.isDisabled ? 0.6 : 1,
                      "&:active": {
                        backgroundColor: state.isDisabled ? "#fff" : "#F3F9FC",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="col-6 invest-select-box">
              <Form.Group className="form-group">
                <Form.Label className="col-label">State</Form.Label>
                <Select
                  placeholder="Select State"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={stateOptions}
                  // value={selectedState}
                  onChange={handleStateChange}
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "#fff",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                      color: "#091F28",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "#F3F9FC",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4} className="col-6 invest-select-box">
              <Form.Group className="form-group">
                <Form.Label className="col-label">City</Form.Label>
                <Select
                  placeholder="Select City"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={cityOptions}
                  onChange={handleCityChange}
                  menuPosition="fixed"
                  styles={{
                    menu: (base) => ({
                      ...base,
                      borderRadius: "15px",
                      marginTop: "8px",
                      backgroundColor: "#fff",
                      padding: "6px",
                      zIndex: "1000",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      borderRadius: "8px",
                      padding: "10px",
                      backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                      color: "#091F28",
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: "#F3F9FC",
                      },
                    }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Second row - Inputs */}
          <Row>
            {inputFields.map((obj, i) => (
              <div className="investment-input-col" key={i}>
                <Form.Group className="form-group">
                  <Form.Label className="col-label input-label">
                    {obj.label}
                  </Form.Label>
                  <Controller
                    name={obj.key}
                    control={control}
                    className="col-input"
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder={`Enter ${obj.placeholder}`}
                        className={`col-input ${
                          errors[obj.key] ? "is-invalid" : ""
                        }`}
                      />
                    )}
                  />
                  {errors[obj.key] && (
                    <div
                      className="invalid-feedback d-block"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {errors[obj.key]?.message}
                    </div>
                  )}
                </Form.Group>
              </div>
            ))}
          </Row>
        </Card>
        {/* Projected Savings */}
        <Card className="investment-card investment-chart-card">
          <div className="saving-title">Projected Retirement Saving</div>
          {investmentDataStore?.investmentReport?.data &&
            investmentDataStore.investmentReport.data.length > 0 &&
            (() => {
              const lastItem =
                investmentDataStore.investmentReport.data[
                  investmentDataStore.investmentReport.data.length - 1
                ];

              return (
                <div className="saving-header">
                  <div className="saving-amount">
                    {formatNumber(Math.floor(lastItem.savings))}
                    <span>By age {formData.retirement_age}</span>
                  </div>

                  {/* Custom Legend */}
                  <div className="custom-legend" onClick={toggleDataset}>
                    <span className="legend-color"></span> Project Balance
                  </div>
                </div>
              );
            })()}

          <div
            style={{ position: "relative", minHeight: "250px", width: "100%" }}
          >
            <Line
              ref={chartRef}
              data={chartData}
              options={{
                layout: {
                  padding: { top: 10, bottom: 10, left: 10, right: 10 },
                },
                elements: {
                  line: { tension: 0.4 },
                  point: { radius: 4 },
                },
                interaction: { mode: "nearest", intersect: false },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                },
                onClick: (event, elements) => {
                  if (!elements.length) return;
                  const index = elements[0].index;
                  const clickedPoint = graphData[index];
                  console.log("Clicked index:", index);
                  console.log("Clicked point:", clickedPoint);
                  setActiveIndex(index);
                },
                scales: {
                  x: {
                    afterFit: function(axis) {
                      axis.paddingRight = 20; // Add 20px space at the end
                    },
                    grid: {
                      display: true,
                      color: "rgba(0, 0, 0, 0.05)",
                    },
                  },
                  y: {
                    min: 0,
                    max:
                      Math.max(...graphData.map((d) => parseFloat(d.value))) *
                      1.02,
                    ticks: {
                      stepSize: Math.max(...graphData.map((d) => d.value)) / 7, // 9 ticks total (0 + 8 steps)
                      callback: function(value) {
                        if (value >= 1000000000) {
                          return (
                            "$" +
                            (value / 1000000000)
                              .toFixed(1)
                              .replace(/\.0$/, "") +
                            "B"
                          );
                        } else if (value >= 1000000) {
                          return (
                            "$" +
                            (value / 1000000).toFixed(1).replace(/\.0$/, "") +
                            "M"
                          );
                        } else if (value >= 1000) {
                          return "$" + (value / 1000).toFixed(0) + "K";
                        }
                        return "$" + value;
                      },
                    },
                    grid: {
                      display: true,
                      color: "rgba(0, 0, 0, 0.05)",
                    },
                  },
                },
              }}
            />
          </div>
        </Card>

        <Card className="investment-stats mb-0">
          {/* Stats Row */}
          {investmentDataStore?.investmentReport?.data &&
            investmentDataStore.investmentReport.data.length > 0 &&
            (() => {
              const lastItem =
                investmentDataStore.investmentReport.data[activeIndex] ||
                investmentDataStore.investmentReport.data[
                  investmentDataStore.investmentReport.data.length - 1
                ];

              return (
                <Row className="text-center">
                  <Col className="col-4">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">
                        Monthly <br />
                        Contribution
                      </div>
                      <div className="stat-value">
                        {formatNumber(
                          Math.floor(lastItem.monthly_contribution),
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col className="col-4">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">
                        Total <br />
                        Contribution
                      </div>
                      <div className="stat-value">
                        {formatNumber(Math.floor(lastItem.total_contributions))}
                      </div>
                    </div>
                  </Col>

                  <Col className="col-4">
                    <div className="investment-stats-wrapper">
                      <div className="stat-label">
                        Investment <br />
                        Growth
                      </div>
                      <div className="stat-value">
                        {formatNumber(Math.floor(lastItem.investment_growth))}
                      </div>
                    </div>
                  </Col>
                </Row>
              );
            })()}
        </Card>
      </div>

      <Card className="investment-table-card pt-0">
        {/* Table */}

        <div className="investment-table">
          {/* Header */}
          <div className="table-header">
            <div className="col text-center">Years</div>
            <div className="col text-center">Amount</div>
          </div>

          {/* Rows */}
          {investmentDataStore.investmentReport.data?.map((row, i) => (
            <div className="table-row" key={i}>
              <div className="col text-center">{row.age}</div>
              <div className="col text-center">
                {formatNumber(Math.floor(row.savings))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default Investment;
